import { Box, Button, makeStyles, TextField, Typography } from '@material-ui/core'
import axios from 'axios'
import React, { useState } from 'react'
import { Modal } from 'react-bootstrap'

const formatLicensePlateInput = (value) => {
  const compact = value.toUpperCase().replace(/[\s-]/g, '')
  if (!/^(?:\d{0,5}|\d{5}[A-Z]{0,2})$/.test(compact)) return value.toUpperCase()
  return `${compact.slice(0, 2)}${compact.length > 2 ? `-${compact.slice(2, 5)}` : ''}${compact.length > 5 ? `-${compact.slice(5)}` : ''}`
}

const useStyles = makeStyles((theme) => ({
  fields: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(3),
    '& .MuiOutlinedInput-notchedOutline': {
      borderBottom: '1px solid rgba(0, 0, 0, 0.23)'
    },
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'minmax(0, 1fr)'
    }
  },
  fullWidth: { gridColumn: '1 / -1' },
  error: {
    marginTop: theme.spacing(2)
  }
}))

const VehicleForm = ({ onClose, onSaved, onBusyChange, vehicle = null }) => {
  const classes = useStyles()
  const isEditing = Boolean(vehicle?._id)
  const [form, setForm] = useState({
    vehicle_id: vehicle?.vehicle_id || '',
    make: vehicle?.make || '',
    model: vehicle?.model || '',
    license_plate: formatLicensePlateInput(vehicle?.license_plate || ''),
    passenger_seats: vehicle?.passenger_seats ?? ''
  })
  const [errors, setErrors] = useState({})
  const [formError, setFormError] = useState('')
  const [saving, setSaving] = useState(false)

  const updateField = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }))
    setErrors((previous) => ({ ...previous, [field]: undefined }))
    setFormError('')
  }

  const saveVehicle = async (event) => {
    event.preventDefault()
    if (saving) return
    const nextErrors = {}
    const plate = form.license_plate.trim().toUpperCase()
    const seats = Number(form.passenger_seats)
    if (!form.vehicle_id.trim() || form.vehicle_id.trim().length > 50)
      nextErrors.vehicle_id = 'Shënoni etiketën në çelësin e automjetit (deri në 50 karaktere).'
    if (!form.make.trim() || form.make.trim().length > 100) nextErrors.make = 'Shënoni markën e automjetit (deri në 100 karaktere).'
    if (!form.model.trim() || form.model.trim().length > 100) nextErrors.model = 'Shënoni modelin e automjetit (deri në 100 karaktere).'
    if (!/^\d{2}-\d{3}-[A-Z]{2}$/.test(plate)) {
      nextErrors.license_plate = 'Përdorni formatin 01-123-AB: 2 shifra, 3 shifra dhe 2 shkronja.'
    }
    if (form.passenger_seats === '' || !Number.isSafeInteger(seats) || seats < 0) {
      nextErrors.passenger_seats = 'Shënoni një numër të plotë, zero ose më të madh.'
    }
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length) return

    setSaving(true)
    onBusyChange(true)
    setFormError('')
    let savedVehicle
    try {
      const payload = {
        vehicle_id: form.vehicle_id.trim(),
        make: form.make.trim(),
        model: form.model.trim(),
        license_plate: plate,
        passenger_seats: seats
      }
      const { data } = isEditing ? await axios.put(`vehicles/${vehicle._id}`, payload) : await axios.post('vehicles', payload)
      if (data.success && data.data) {
        savedVehicle = data.data
      } else {
        setFormError(data.message || 'Automjeti nuk u ruajt. Provoni përsëri.')
        setErrors(data.errors || {})
      }
    } catch (error) {
      const response = error.response
      setFormError(
        response?.status === 403
          ? 'Nuk keni të drejtë të ndryshoni automjetet.'
          : response?.data?.message || 'Automjeti nuk u ruajt. Provoni përsëri.'
      )
      setErrors(response?.data?.errors || {})
    } finally {
      setSaving(false)
      onBusyChange(false)
    }
    if (savedVehicle) onSaved(savedVehicle)
  }

  return (
    <form onSubmit={saveVehicle} noValidate aria-busy={saving}>
      <Modal.Body>
        <Box className={classes.fields}>
          <TextField
            id='vehicle-key-id'
            label='Vehicle ID (çelësi)'
            placeholder='Vehicle ID 1'
            variant='outlined'
            fullWidth
            required
            disabled={saving}
            value={form.vehicle_id}
            onChange={(event) => updateField('vehicle_id', event.target.value)}
            inputProps={{ maxLength: 50 }}
            error={Boolean(errors.vehicle_id)}
            helperText={errors.vehicle_id}
            className={classes.fullWidth}
          />
          <TextField
            id='vehicle-make'
            label='Marka'
            variant='outlined'
            fullWidth
            required
            disabled={saving}
            value={form.make}
            onChange={(event) => updateField('make', event.target.value)}
            inputProps={{ maxLength: 100 }}
            error={Boolean(errors.make)}
            helperText={errors.make}
          />
          <TextField
            id='vehicle-model'
            label='Modeli'
            variant='outlined'
            fullWidth
            required
            disabled={saving}
            value={form.model}
            onChange={(event) => updateField('model', event.target.value)}
            inputProps={{ maxLength: 100 }}
            error={Boolean(errors.model)}
            helperText={errors.model}
          />
          <TextField
            id='vehicle-license-plate'
            label='Numri i targës'
            variant='outlined'
            fullWidth
            required
            disabled={saving}
            value={form.license_plate}
            onChange={(event) => updateField('license_plate', formatLicensePlateInput(event.target.value))}
            placeholder='01-123-AB'
            inputProps={{ maxLength: 9, pattern: '[0-9]{2}-[0-9]{3}-[A-Z]{2}', autoCapitalize: 'characters' }}
            error={Boolean(errors.license_plate)}
            helperText={errors.license_plate}
          />
          <TextField
            id='vehicle-passenger-seats'
            label='Vendet për pasagjerë'
            type='number'
            variant='outlined'
            fullWidth
            required
            disabled={saving}
            value={form.passenger_seats}
            onChange={(event) => updateField('passenger_seats', event.target.value)}
            inputProps={{ min: 0, step: 1 }}
            error={Boolean(errors.passenger_seats)}
            helperText={errors.passenger_seats || 'Numri i vendeve pa përfshirë vendin e shoferit.'}
          />
        </Box>
        {formError && (
          <Typography role='alert' color='error' className={classes.error}>
            {formError}
          </Typography>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose} disabled={saving}>
          Kthehu te lista
        </Button>
        <Button type='submit' variant='contained' color='primary' disabled={saving}>
          {saving ? 'Duke ruajtur…' : 'Ruaj automjetin'}
        </Button>
      </Modal.Footer>
    </form>
  )
}

export default VehicleForm
